import { useCallback, useMemo } from 'react';
import { SelectMenuProps } from 'src/components/SelectMenu';
import {
  CustomAttributeOption,
  CustomAttributeValue,
} from 'src/graphql/entities/custom-attributes/custom-attributes.types';
import { CustomAttributeEditProps } from 'src/graphql/entities/custom-attributes/field-view-registry/types';
import { toSelectMenuOptions } from 'src/graphql/entities/custom-attributes/field-view-registry/utils';

/**
 * Everything the single- and multi-select attribute editors present the same
 * way. Only the value shape differs between them, so the menu's own
 * configuration lives here rather than being restated per editor.
 */
export const ATTRIBUTE_SELECT_MENU_PROPS = {
  search: true,
  emptyText: 'No options found.',
  variant: 'inline',
} satisfies Partial<SelectMenuProps>;

/** For editors that keep the menu's default trigger as the row's value control. */
export const ATTRIBUTE_SELECT_TRIGGER_PROPS = {
  clearable: true,
  // Fills the row's own 2rem floor so the control does not sit squashed inside it.
  sx: { width: '100%', minHeight: '2rem' },
} satisfies Partial<SelectMenuProps>;

export const useAttributeMenuOptions = (options: CustomAttributeOption[]) =>
  useMemo(() => toSelectMenuOptions(options), [options]);

/** Writes the edited value through the row, which owns the debounce and the save. */
export const useAttributeCommit = ({
  onChange,
  onSave,
}: Pick<CustomAttributeEditProps, 'onChange' | 'onSave'>) =>
  useCallback(
    (value: CustomAttributeValue) => {
      onChange(value);
      onSave(value);
    },
    [onChange, onSave],
  );
